/**
 * Every word the Help & feedback panel shows (SA-1310; wireframe decisions 7 to 23). Components hold
 * no copy of their own: a change of wording is a change here.
 */
import { getBranding } from '@/lib/branding'

export const TITLE = 'Help & feedback'
export const TAB_ASK = 'Ask'
export const TAB_MINE = 'Your feedback'
export const GRABBER = 'Drag down to close'
export const SPEAKER_HELP = 'Help'
export const SPEAKER_YOU = 'You'
export const THINKING = 'Thinking'

/** The entry points (SA-1306 spec §2). */
export const ENTRY_LABEL = 'Help & feedback'
export const ENTRY_TIP = 'Help & feedback (?)'
export const TELL_US = 'Tell us what happened'

const APP_NAME_FALLBACK = 'Secret Agents AI'

/** A page's title without the app's name: "Q-0142 · Quotes · App" → "Q-0142 · Quotes". */
export function pageWords(title: string | null | undefined, appName: string = getBranding().appName ?? APP_NAME_FALLBACK): string {
  const text = (title ?? '').trim()
  if (!text || text === appName) return ''
  const tail = ` · ${appName}`
  return text.endsWith(tail) ? text.slice(0, -tail.length).trim() : text
}

/** The opening from the page: names the page when it has a title. */
export function openingLine(page?: string): string {
  return page ? `You're on ${page}. What's going on?` : "What's going on?"
}

/** The opening from a failure toast, quoting the toast's own words. */
const FAILURE_QUESTION = 'Want to tell us about it, or should I explain what happened?'
const OPENING_MAX = 160
/** A message that already says it failed, anywhere in it, is used as written (SA-1306 spec §2.5). */
const SAYS_IT_FAILED = /\b(failed|couldn['’]t|could not|can['’]t|cannot|unable|error|went wrong)\b/i

export function failureOpening(message: string): string {
  let what = message.trim().replace(/\s+/g, ' ').replace(/[.!:]+$/, '')
  if (what.length > OPENING_MAX) what = `${what.slice(0, OPENING_MAX - 1).trimEnd()}…`
  if (SAYS_IT_FAILED.test(what)) return `${what}. ${FAILURE_QUESTION}`
  return `${what} just failed. ${FAILURE_QUESTION}`
}

export type ChipKey = 'broken' | 'howTo' | 'idea' | 'tellTeam' | 'explain'

export const CHIP_WORDS: Record<ChipKey, string> = {
  broken: "Something isn't working",
  howTo: 'How do I…',
  idea: 'I have an idea',
  tellTeam: 'Tell the team',
  explain: 'Explain what happened',
}

/** The one line a chip answers with locally; `explain` is sent as a turn instead. */
export const CHIP_REPLIES: Record<Exclude<ChipKey, 'explain'>, string> = {
  broken: 'Sorry about that. Tell me what you saw, in your own words.',
  howTo: 'Ask away. I answer from the user guide and point you to the place.',
  idea: 'Go ahead. What would make this better?',
  tellTeam: 'Thanks. What were you trying to do when it failed?',
}

export const PAGE_CHIPS: ChipKey[] = ['broken', 'howTo', 'idea']
export const FAILURE_CHIPS: ChipKey[] = ['tellTeam', 'explain']
/** The chips that ask the assistant for an answer, hidden when answers are off (SA-1306 §3). */
export const ANSWER_CHIPS: ChipKey[] = ['howTo', 'explain']

export const TAKE_ME_THERE = 'Take me there'
export const DID_IT_SORT = 'Did that sort it?'
export const YES = 'Yes'
/** Plays the answer's task tour on the real screen (SA-1316). */
export const SHOW_ME = 'Show me'
export const NOT_QUITE = 'Not quite'
export const GUIDANCE_THANKS = "Glad that sorted it. I've noted the question so the team can see what people ask."
export const NOT_QUITE_LEAD = "Sorry it didn't. I'll pass it to the team; change anything first."
/** After an answer the help could not give (SA-1313). */
export const NOT_FOUND_LEAD = "I couldn't find that in the help yet. I'll pass your question to the team; change anything first, or ask another way."
/** When the help has no answer and the person can send in one tap (SA-1314). */
export const NOT_FOUND_OFFER = "I couldn't find that in the help yet. Want me to pass the question to the team?"
export const ASK_ANOTHER_WAY = 'Ask another way'
export const ASK_ANOTHER_WAY_LINE = "Try putting it another way, or name the page or the step you're on."
/** The sources under an answer: a help page it read, and an app page it points to. */
export const SOURCES_LABEL = 'Sources'
export const FROM_SOURCE = 'From'
export const GO_TO_SOURCE = 'Go to'
export const TURN_FAILED = "I couldn't think that through just now. You can still send what you wrote."
export const VIEW_AS_LINE = 'Feedback goes out under your own name. Stop viewing as someone else to send it.'

export const CARD_LEAD = "Here's what I'll send to the team. Change anything first."
export const CARD_UPDATED_LEAD = "Added. Here's the updated summary."
export const KIND_LABEL = 'Kind'
export const SUMMARY_LABEL = 'Summary'
export const ATTEMPTING_LABEL = 'You were trying to:'
export const SEND = 'Send to the team'
export const ADD_MORE = 'Add more'

export type CardKind = 'problem' | 'idea' | 'question' | 'praise'
export const CARD_KINDS: CardKind[] = ['problem', 'idea', 'question', 'praise']
export const KIND_WORDS: Record<CardKind | 'guidance', string> = {
  problem: 'Problem',
  idea: 'Idea',
  question: 'Question',
  praise: 'Praise',
  guidance: 'Question answered',
}

export const NOTHING_ATTACHED = 'Nothing attached'
export const NOTHING_ATTACHED_OPEN = 'Nothing attached. The team sees only what you write.'
export const SHOW_ATTACHED = 'Show what is attached'
export const HIDE_ATTACHED = 'Hide what is attached'
export const removeChipLabel = (label: string) => `Remove ${label}`
export const previewChipLabel = (label: string) => `Show what ${label.toLowerCase()} carries`

export const MESSAGE_LABEL = 'Message'
export const PLACEHOLDER = 'Ask, or tell us what happened…'
export const PLACEHOLDER_MORE = 'Add to what you told us…'
export const SEND_MESSAGE = 'Send message'
export const COMPOSER_HINT = 'Enter to send · Shift+Enter for a new line'

export const MIC_LABEL = 'Talk instead of typing'
export const MIC_STOP = 'Stop listening'
export const MIC_EXPLAIN =
  'Talk instead of typing? Your browser will ask to use the microphone. What you say becomes text you can edit before sending; no recording is kept.'
export const MIC_CONTINUE = 'Continue'
export const MIC_NOT_NOW = 'Not now'
export const LISTENING = 'Listening. Stops when you pause, click the mic, or press Esc.'
export const MIC_BLOCKED = "Your browser blocked the microphone, so I've hidden it for now. Typing works as before."
export const MIC_BLOCKED_HOW =
  "To talk instead, allow the microphone for this site in the browser's address bar, then reopen this panel."

export function receiptLine(reference: string): string {
  return `Sent, thank you. Reference ${reference}.`
}
export const CLOSING_SOON = 'Closing in a few seconds.'
export const KEEP_OPEN = 'Keep open'
export const START_NEW = 'Start something new'
export const CLOSE = 'Close'

export function sendFailedLine(reason: string): string {
  return `Couldn't send. ${reason} Your words are kept here.`
}
export const TRY_AGAIN = 'Try again'

/** A refusal's own words for a code the panel knows, else the connection's. */
export const REFUSAL_REASONS: Record<string, string> = {
  already_sent: 'This was already sent.',
  context_not_allowed: 'Something attached could not be sent; remove it and try again.',
  view_as_forbidden: 'Stop viewing as someone else to send it.',
  assistant_unavailable: 'The assistant is not set up here.',
}
export const CONNECTION_DROPPED = 'The connection dropped.'

export const RESUME_LEAD = 'Pick up where you left off?'
export const KEEP_GOING = 'Keep going'
export const SEND_AS_IS = 'Send as it is'
export const DISCARD = 'Discard'
export const DISCARDED = 'Discarded. Nothing was sent.'
export const RESUME_CUT = 120

/** The person's first words, cut at 120 characters with an ellipsis. */
export function resumeQuote(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length > RESUME_CUT ? `${t.slice(0, RESUME_CUT).trimEnd()}…` : t
}

/** "just now", "20 minutes ago", "3 hours ago", "2 days ago". */
export function agoText(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

/** The resume offer's second line: "“…” · Quotes · 20 minutes ago". */
export function resumeLine(quote: string, page: string, ago: string): string {
  return [`“${quote}”`, page, ago].filter(Boolean).join(' · ')
}

export const PLAIN_LEAD = "Tell us what happened or what you'd like. It goes straight to the team."
export const PLAIN_LABEL = 'What happened'
export const PLAIN_PLACEHOLDER = 'What were you trying to do, and what did you see?'
export const PLAIN_HELP_LEAD = 'Looking for how to do something?'
export const PLAIN_HELP_LINK = 'Help & docs has the user guide.'

export type ReportStatus = 'new' | 'triaged' | 'planned' | 'done' | 'not_planned'
export type StatusTone = 'neutral' | 'info-soft' | 'success-soft' | 'muted-soft'
export const STATUS_WORDS: Record<ReportStatus, { label: string; tone: StatusTone }> = {
  new: { label: 'Received', tone: 'neutral' },
  triaged: { label: 'Looking into it', tone: 'info-soft' },
  planned: { label: 'Planned', tone: 'info-soft' },
  done: { label: 'Done', tone: 'success-soft' },
  not_planned: { label: 'Not planned', tone: 'muted-soft' },
}
export const MINE_PRIVACY = 'Only you and the product team see these.'
export const MINE_EMPTY = 'Nothing sent yet. What you send shows here, with where it stands.'
export const MINE_FAILED = "Couldn't load your feedback."
export const MINE_LOADING = 'Loading your feedback'

/** The `{reason}` of a failed send. */
export function refusalReason(code: string | null): string {
  return (code && REFUSAL_REASONS[code]) || CONNECTION_DROPPED
}
